import { createFileRoute } from "@tanstack/react-router";
import { AppShell } from "@/components/app-shell";
import { getSession, isPrimary, requireRole } from "@/lib/session";
import { LESSONS, SUBJECTS, QUIZZES } from "@/lib/mock-data";
import { Trophy, Flame, Clock, Target } from "lucide-react";
import { tintBg } from "./learner.index";
import { LearnerIntelligencePanel } from "@/components/intelligence/learner-intelligence-panel";
import { LearnerProgressSummaryPanel } from "@/components/intelligence/learner-progress-summary-panel";

export const Route = createFileRoute("/learner/progress")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "My Progress — MAKAI" }] }),
  component: ProgressPage,
});

function ProgressPage() {
  const s = getSession();
  const level = isPrimary(s?.grade) ? "primary" : "secondary";
  const lessons = LESSONS.filter((l) => l.level === level);
  const bySubject = SUBJECTS.filter((sub) => sub.level === level || sub.level === "both").map((sub) => {
    const ls = lessons.filter((l) => l.subjectId === sub.id);
    const avg = ls.length ? Math.round(ls.reduce((a, b) => a + b.progress, 0) / ls.length) : 0;
    return { sub, avg, count: ls.length };
  });
  const week = [40, 65, 30, 80, 55, 90, 70]; // mock minutes per day

  const stats = [
    { k: "1,240", v: "XP earned", icon: Trophy, tint: "gold" as const },
    { k: "6 days", v: "Current streak", icon: Flame, tint: "primary" as const },
    { k: "4h 28m", v: "This week", icon: Clock, tint: "sky" as const },
    { k: `${QUIZZES.filter(q => q.bestScore).length}`, v: "Quizzes done", icon: Target, tint: "gold" as const },
  ];

  return (
    <AppShell role="learner" title="My Progress" subtitle="A clear view of your learning journey.">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.v} className="rounded-2xl border border-border bg-card p-5 shadow-soft">
            <div className={`grid h-10 w-10 place-items-center rounded-xl ${tintBg(s.tint)}`}>
              <s.icon className="h-5 w-5" />
            </div>
            <div className="mt-3 font-display text-2xl font-bold">{s.k}</div>
            <div className="text-xs text-muted-foreground">{s.v}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
          <h3 className="font-display text-lg font-bold">By subject</h3>
          <div className="mt-4 space-y-4">
            {bySubject.map(({ sub, avg, count }) => (
              <div key={sub.id}>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className={`grid h-7 w-7 place-items-center rounded-md ${tintBg(sub.color)}`}>
                      <sub.icon className="h-3.5 w-3.5" />
                    </div>
                    <span className="font-medium">{sub.name}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{avg}% · {count} lessons</span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${avg}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
          <h3 className="font-display text-lg font-bold">This week</h3>
          <p className="text-xs text-muted-foreground">Minutes spent learning per day.</p>
          <div className="mt-6 flex h-44 items-end justify-between gap-2">
            {week.map((m, i) => (
              <div key={i} className="flex flex-1 flex-col items-center gap-2">
                <div className="flex w-full flex-1 items-end">
                  <div
                    className="w-full rounded-t-lg bg-gradient-to-t from-primary to-gold"
                    style={{ height: `${(m / 100) * 100}%` }}
                    title={`${m} min`}
                  />
                </div>
                <span className="text-[10px] font-semibold text-muted-foreground">{["M","T","W","T","F","S","S"][i]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <LearnerProgressSummaryPanel />
      <LearnerIntelligencePanel />
    </AppShell>
  );
}
